import "../App.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentWeather, getCurrentLocation } from "../Utils/weather.tsx";
import { T } from "../tokens";
import Butterfly from "../Classes/Butterfly.tsx";


class SurveyLocation {
  public time: number;
  public location: GeolocationPosition;
  constructor(time: number, location: GeolocationPosition) {
    this.time = time;
    this.location = location;
  }
}

interface ButterflySightings {
  speciesId: number;
  sectionSightings: Array<number>;
  total: number;
}

// ─── Icons ─────────────────────────────────────────────────────────────
const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowRight = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PlusIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);
const MinusIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const CloseIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const PauseIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <rect x="7" y="5" width="3.5" height="14" rx="1" fill="currentColor" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="1" fill="currentColor" />
  </svg>
);
const PlayIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 5v14l12-7z" />
  </svg>
);
const CheckIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const StarIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill={T.honey}>
    <path d="M12 2.5l2.9 6.5 7.1.7-5.4 4.8 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.7l7.1-.7L12 2.5z" stroke={T.honey} strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

// ─── Floating timer pill ───────────────────────────────────────────────
function FloatingTimer({ seconds, running, onToggle, total, onDone }: {
  seconds: number; running: boolean; onToggle: () => void; total: number; onDone: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const fmt = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} style={{
        position: 'absolute', right: 16, bottom: 16,
        width: 52, height: 52, borderRadius: 9999, border: 'none',
        background: T.ink, color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: T.shadowFloat,
      }}>{running ? <PauseIcon /> : <PlayIcon />}</button>
    );
  }

  return (
    <div style={{
      position: 'absolute', left: 16, right: 16, bottom: 16,
      background: T.ink, color: '#fff',
      borderRadius: 9999, padding: '6px 6px 6px 18px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: T.shadowFloat,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: 9999,
        background: running ? '#7BC074' : T.honey,
        boxShadow: running ? '0 0 0 3px rgba(123,192,116,0.25)' : 'none',
        flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.fontMono, fontSize: 18, fontWeight: 600, letterSpacing: '0.02em', lineHeight: 1 }}>{fmt(seconds)}</div>
        <div style={{ fontFamily: T.font, fontSize: 11, opacity: 0.7, marginTop: 2 }}>{total} total · {running ? 'running' : 'paused'}</div>
      </div>
      <button onClick={onToggle} style={{
        width: 44, height: 44, borderRadius: 9999, border: 'none',
        background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{running ? <PauseIcon /> : <PlayIcon />}</button>
      <button onClick={() => setCollapsed(true)} style={{
        width: 44, height: 44, borderRadius: 9999, border: 'none',
        background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><CloseIcon /></button>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────
const SurveyRecording = () => {
  const [userId, setUserId] = useState<any>();
  const [surveyType, setSurveyType] = useState<string>('pollard');
  const [numSections, setNumSections] = useState(5);
  const [surveyName, setSurveyName] = useState('');
  const [startTime, setStartTime] = useState(0);
  const [butterflies, setButterflies] = useState<Array<Butterfly>>([]);
  const [favoriteButterflies, setFavoriteButterflies] = useState<Array<Butterfly>>([]);
  const [sightings, setSightings] = useState<Record<number, ButterflySightings>>({});
  const [locationsTracked, setLocationsTracked] = useState<Array<SurveyLocation>>([]);
  const [curSection, setCurrentSection] = useState(0);
  const [search, setSearch] = useState('');
  const [seenFilter, setSeenFilter] = useState(false);
  const [favoriteNameMode, setFavoriteNameMode] = useState<'common' | 'scientific'>('common');
  const [stateLocation, setStateLocation] = useState('');
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const elapsedBeforeRunRef = useRef(0);
  const runStartedAtRef = useRef<number | null>(null);
  const [locationWorking, setLocationWorking] = useState<'unknown' | 'working' | 'not-working'>('unknown');
  const navigate = useNavigate();

  // Timer
  useEffect(() => {
    if (!timerRunning) {
      runStartedAtRef.current = null;
      elapsedBeforeRunRef.current = secondsElapsed;
      return;
    }

    elapsedBeforeRunRef.current = secondsElapsed;
    runStartedAtRef.current = Date.now();

    const updateElapsed = () => {
      if (runStartedAtRef.current === null) return;
      const elapsedThisRun = Math.floor((Date.now() - runStartedAtRef.current) / 1000);
      setSecondsElapsed(elapsedBeforeRunRef.current + elapsedThisRun);
    };

    updateElapsed();
    const id = setInterval(updateElapsed, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerRunning]);

  const getCurrentSecondsElapsed = () => {
    if (!timerRunning || runStartedAtRef.current === null) return secondsElapsed;
    return elapsedBeforeRunRef.current + Math.floor((Date.now() - runStartedAtRef.current) / 1000);
  };

  const handleTimerToggle = () => {
    if (timerRunning && runStartedAtRef.current !== null) {
      const currentElapsed = getCurrentSecondsElapsed();
      elapsedBeforeRunRef.current = currentElapsed;
      setSecondsElapsed(currentElapsed);
    }
    setTimerRunning(r => !r);
  };

  // Sightings helpers
  const getCount = (id: number): number => {
    const s = sightings[id];
    if (!s) return 0;
    return surveyType === 'pollard' ? (s.sectionSightings[curSection] || 0) : s.total;
  };

  const bump = (id: number, delta: number) => {
    setSightings(prev => {
      const existing = prev[id] || {
        speciesId: id,
        sectionSightings: new Array(numSections).fill(0),
        total: 0,
      };
      if (surveyType === 'pollard') {
        const sects = [...existing.sectionSightings];
        while (sects.length < numSections) sects.push(0);
        sects[curSection] = Math.max(0, (sects[curSection] || 0) + delta);
        const total = sects.reduce((a, b) => a + b, 0);
        return { ...prev, [id]: { ...existing, sectionSightings: sects, total } };
      } else {
        const total = Math.max(0, existing.total + delta);
        return { ...prev, [id]: { ...existing, total } };
      }
    });
  };

  // Totals
  const sectionTotal = Object.values(sightings).reduce((acc, s) => {
    return acc + (surveyType === 'pollard' ? (s.sectionSightings[curSection] || 0) : 0);
  }, 0);
  const grandTotal = Object.values(sightings).reduce((acc, s) => acc + s.total, 0);

  // Filtered list
  const visibleList = useMemo(() => {
    let xs = butterflies;
    if (stateLocation) xs = xs.filter(b => !b.states || b.states.includes(stateLocation));
    if (search) xs = xs.filter(b => {
      const query = search.toLowerCase();
      return (
        b.commonName.toLowerCase().includes(query) ||
        b.scientificName.toLowerCase().includes(query) ||
        (b.commonFamilyName && b.commonFamilyName.toLowerCase().includes(query)) ||
        (b.scientificFamilyName && b.scientificFamilyName.toLowerCase().includes(query)) ||
        (b.commonSubfamilyName && b.commonSubfamilyName.toLowerCase().includes(query)) ||
        (b.scientificSubfamilyName && b.scientificSubfamilyName.toLowerCase().includes(query))
      );
    });
    if (seenFilter) xs = xs.filter(b => getCount(b.id) > 0);
    return xs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [butterflies, search, seenFilter, sightings, curSection, stateLocation]);

  const fetchButterflies = () => {
    fetch('api/butterflies')
      .then(r => r.json())
      .then((data: any[]) => {
        const list: Butterfly[] = data.map(d =>
          new Butterfly(d.id, d.commonName, d.scientificName, d.commonFamilyName, d.scientificFamilyName, d.commonSubfamilyName, d.scientificSubfamilyName, d.states)
        );
        setButterflies(list);
      })
      .catch(err => {
        console.error("Error fetching butterflies:", err);
        alert("Error fetching butterflies: " + err.message);
      });
  };

  const fetchFavorites = (userId: number, survey: any) => {
        fetch(`api/butterflies/favorites/${userId}`)
            .then((response) => response.json())
            .then((data) => {
                let favorites: Array<Butterfly> = [];
                let dataLen = data.length;
                for(let i = 0; i < dataLen; i++) {
                    if(data[i].butterfly.states.includes(survey.location)) {favorites.push(data[i].butterfly)};
                }
                setFavoriteButterflies(favorites);
            })
            .catch((error) => {
                console.error("Error fetching favorite butterflies:", error);
                alert("Error fetching favorite butterflies: " + error.message); // Display alert if there's an error
            });
    }

  const pushLocation = (location: GeolocationPosition) => {
    setLocationsTracked(prev => [...prev, new SurveyLocation(secondsElapsed, location)]);
  };

  // Geolocation every 2 minutes
  useEffect(() => {
    if (secondsElapsed > 0 && secondsElapsed % 120 === 0) {
      navigator.geolocation.getCurrentPosition(
        pushLocation,
        () => setLocationWorking('not-working'),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsElapsed]);

  // Unload warning
  useEffect(() => {
    const cb = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", cb);
    return () => window.removeEventListener("beforeunload", cb);
  }, []);

  // Init
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") as string);
    const survey = JSON.parse(localStorage.getItem("survey") as string);
    if (user?.userId) { setUserId(user.userId); } else { localStorage.clear(); navigate("/"); return; }
    if (survey?.type) {
      setSurveyType(survey.type);
      setStateLocation(survey.location || '');
      setNumSections(survey.numSections || 5);
      setSurveyName(survey.name || '');
    } else { navigate("/survey"); return; }
    setStartTime(Math.floor(Date.now() / 1000));
    setTimerRunning(true);
    fetchButterflies();
    fetchFavorites(user.userId, survey);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocationsTracked([new SurveyLocation(0, pos)]);
        setLocationWorking('working');
      },
      () => setLocationWorking('not-working'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const onSubmit = async () => {
      const coords = await getCurrentLocation();
      const weatherEnd = coords ? await getCurrentWeather(coords.latitude, coords.longitude) : null;
      const survey = JSON.parse(localStorage.getItem("survey") as string);
      const finalSecondsElapsed = getCurrentSecondsElapsed();

      if (!survey) {
        alert("No survey found in localStorage");
        return;
      }

      const surveyId = survey.surveyId ?? survey.id;
      if (!surveyId) {
        alert("No survey ID found. Start the survey first.");
        return;
      }

      const submitRequest: any = {
        startTime,
        endTime: startTime + finalSecondsElapsed,
        secondsElapsed: finalSecondsElapsed,
        weatherEnd,
        butterflySightings: Object.values(sightings),
        locations: locationsTracked.map((loc) => ({
          time: loc.time,
          latitude: loc.location.coords.latitude,
          longitude: loc.location.coords.longitude,
        })),
      };

      try {
        const response = await fetch(`api/surveys/${surveyId}/submitAll?userId=${userId}`, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submitRequest),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || "Survey submission failed");
        }

        localStorage.removeItem("survey");
        localStorage.setItem("surveyId", surveyId);
        navigate("/summary");
      } catch (err: any) {
        console.error(err);
        alert("Error submitting survey: " + err.message);
      }
  };

  const sectionLabels = Array.from({ length: numSections }, (_, i) => String.fromCharCode(65 + i));
  const hasSightingsInSection = (i: number) =>
    Object.values(sightings).some(s => (s.sectionSightings[i] || 0) > 0);

  return (
    <div className="lep-recording">
      <div className="lep-recording-inner">

        {/* ── Top: header + section stepper ── */}
        <div style={{ padding: '54px 16px 0', background: T.bg, flexShrink: 0 }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <button onClick={() => navigate('/survey')} style={{
              width: 36, height: 36, border: 'none', background: 'transparent',
              borderRadius: T.r.pill, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.ink, cursor: 'pointer',
            }}><ArrowLeft /></button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {surveyType} · {surveyName || 'Survey'}
              </div>
              <div style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: T.ink, letterSpacing: '-0.01em' }}>Recording sightings</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <div style={{
                padding: '8px 12px', borderRadius: T.r.pill, display: 'flex', alignItems: 'center', gap: 8,
                background: locationWorking === 'working' ? '#E7F3FF' : locationWorking === 'not-working' ? '#FDE7E7' : T.card,
                border: `1px solid ${locationWorking === 'working' ? '#7DB7FF' : locationWorking === 'not-working' ? '#E08A8A' : T.line}`,
                color: locationWorking === 'working' ? '#175A9B' : locationWorking === 'not-working' ? '#A12A2A' : T.ink2,
                fontFamily: T.font, fontSize: 12, fontWeight: 600,
              }}>
                <span>{locationWorking === 'working' ? 'Location enabled' : locationWorking === 'not-working' ? 'Location unavailable' : 'Checking location…'}</span>
                <span style={{ fontFamily: T.fontDisplay, fontSize: 14, lineHeight: 1 }}>{locationWorking === 'working' ? '✓' : '✕'}</span>
              </div>
              <button onClick={onSubmit} style={{
                height: 36, padding: '0 14px', borderRadius: 9999, border: 'none',
                background: T.forest, color: '#fff', fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Finish</button>
            </div>
          </div>

          {/* Section stepper (Pollard only) */}
          {surveyType === 'pollard' && (
            <div style={{
              background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.lg,
              padding: 6, display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: T.shadowCard, marginBottom: 0,
            }}>
              <button onClick={() => setCurrentSection(Math.max(0, curSection - 1))} disabled={curSection === 0} style={{
                width: 44, height: 48, border: 'none', background: 'transparent',
                color: curSection === 0 ? T.ink3 : T.ink, cursor: curSection === 0 ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: T.r.md,
              }}><ArrowLeft /></button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.ink2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Section</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'center' }}>
                  <span style={{ fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700, color: T.forest, letterSpacing: '-0.03em', lineHeight: 1 }}>{sectionLabels[curSection]}</span>
                  <span style={{ fontFamily: T.font, fontSize: 12, color: T.ink2 }}>· {sectionTotal} {sectionTotal === 1 ? 'sighting' : 'sightings'}</span>
                </div>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 4 }}>
                  {sectionLabels.map((_, i) => (
                    <div key={i} style={{
                      width: i === curSection ? 16 : 6, height: 6, borderRadius: 9999,
                      background: i === curSection ? T.forest : (hasSightingsInSection(i) ? T.ink3 : T.bgDeep),
                      transition: 'all 0.2s',
                    }} />
                  ))}
                </div>
              </div>
              <button onClick={() => setCurrentSection(Math.min(numSections - 1, curSection + 1))} disabled={curSection === numSections - 1} style={{
                width: 44, height: 48, border: 'none', background: 'transparent',
                color: curSection === numSections - 1 ? T.ink3 : T.ink, cursor: curSection === numSections - 1 ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: T.r.md,
              }}><ArrowRight /></button>
            </div>
          )}
        </div>

        {/* ── Favorites ── */}
        {favoriteButterflies.length > 0 && (
          <div style={{ padding: '16px 0 8px', flexShrink: 0, background: T.bg }}>
            <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>              
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <StarIcon />
                <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.ink2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Favorite Butterflies</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontFamily: T.font, fontSize: 11, color: T.ink3 }}>Show:</span>
                <button onClick={() => setFavoriteNameMode('common')} style={{
                  height: 28, borderRadius: 9999, border: `1px solid ${favoriteNameMode === 'common' ? T.forest : T.line}`,
                  background: favoriteNameMode === 'common' ? T.forest : T.card,
                  color: favoriteNameMode === 'common' ? '#fff' : T.ink2,
                  fontFamily: T.font, fontSize: 11, fontWeight: 600, padding: '0 10px', cursor: 'pointer'
                }}>Common</button>
                <button onClick={() => setFavoriteNameMode('scientific')} style={{
                  height: 28, borderRadius: 9999, border: `1px solid ${favoriteNameMode === 'scientific' ? T.forest : T.line}`,
                  background: favoriteNameMode === 'scientific' ? T.forest : T.card,
                  color: favoriteNameMode === 'scientific' ? '#fff' : T.ink2,
                  fontFamily: T.font, fontSize: 11, fontWeight: 600, padding: '0 10px', cursor: 'pointer'
                }}>Scientific</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '0 16px', overflowX: 'auto', paddingBottom: 4 }}>
              {favoriteButterflies.map(b => {
                const n = getCount(b.id);
                const isUnknown = b.commonName === 'Unknown';
                const favoriteLabel = favoriteNameMode === 'scientific' ? b.scientificName || b.commonName : b.commonName;
                const unknownDetails = favoriteNameMode === 'scientific'
                  ? [b.scientificFamilyName || 'Unknown', b.scientificSubfamilyName || 'Unknown']
                  : [b.commonFamilyName || 'Unknown', b.commonSubfamilyName || 'Unknown'];
                return (
                  <button key={b.id} onClick={() => bump(b.id, 1)} style={{
                    flexShrink: 0, width: 120, padding: 10, borderRadius: T.r.md,
                    background: n > 0 ? T.forestSoft : T.card,
                    border: `1.5px solid ${n > 0 ? T.forest : T.line}`,
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: T.forest, opacity: 0.7 }} />
                      {n > 0 && (
                        <div style={{
                          marginLeft: 'auto', minWidth: 28, height: 22, padding: '0 8px',
                          borderRadius: 9999, background: T.forest, color: '#fff',
                          fontFamily: T.fontDisplay, fontSize: 13, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{n}</div>
                      )}
                    </div>
                    <div style={{
                      fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.ink, marginTop: 8, lineHeight: 1.2,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    } as any}>
                      {isUnknown ? (
                        <div style={{ height: 42, overflow: 'hidden' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.15 }}>Unknown</div>
                          {unknownDetails.map((line, idx) => (
                            <div key={`${line}-${idx}`} style={{
                              fontSize: 9.5, fontWeight: 500, lineHeight: 1.15,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {line}
                            </div>
                          ))}
                        </div>
                      ) : favoriteLabel}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Search & filter ── */}
        <div style={{ padding: '8px 16px', display: 'flex', gap: 8, background: T.bg, flexShrink: 0 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{
              display: 'flex', alignItems: 'center',
              background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`,
              height: 48, paddingLeft: 14,
            }}>
              <span style={{ color: T.ink3, display: 'flex', flexShrink: 0 }}><SearchIcon /></span>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search species, family, or subfamily"
                style={{
                  flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent',
                  fontFamily: T.font, fontSize: 16, color: T.ink, padding: '0 10px',
                  letterSpacing: '-0.01em', minWidth: 0,
                }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{
                  width: 36, height: 48, border: 'none', background: 'transparent', color: T.ink3, cursor: 'pointer',
                }}><CloseIcon /></button>
              )}
            </div>
          </div>
          <button onClick={() => setSeenFilter(f => !f)} style={{
            height: 48, padding: '0 14px', borderRadius: T.r.md,
            background: seenFilter ? T.ink : T.card,
            color: seenFilter ? '#fff' : T.ink,
            border: `1px solid ${seenFilter ? T.ink : T.line}`,
            fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <CheckIcon /> Seen
          </button>
        </div>

        {/* ── Species list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 96px', background: T.bg }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visibleList.map(b => {
              const n = getCount(b.id);
              const active = n > 0;
              return (
                <div key={b.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: T.card,
                  border: `1px solid ${active ? T.forest : T.line}`,
                  borderRadius: T.r.md, padding: '8px 8px 8px 12px',
                  boxShadow: active ? '0 0 0 3px rgba(45,80,22,0.06)' : 'none',
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: T.forest, opacity: 0.6, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        fontFamily: T.font, fontSize: 14.5, fontWeight: 600, color: T.ink, letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{b.commonName}</span>
                    </div>
                    <div style={{
                      fontFamily: T.fontSerif, fontStyle: 'italic', fontSize: 12, color: T.ink2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{b.scientificName}</div>
                    <div style={{
                      fontFamily: T.fontSerif, fontSize: 12, color: T.ink,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{b.commonSubfamilyName}</div>
                    <div style={{
                      fontFamily: T.fontSerif, fontSize: 12, color: T.ink,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{b.commonFamilyName}</div>
                  </div>
                  {/* Tally controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => bump(b.id, -1)} disabled={n === 0} style={{
                      width: 40, height: 40, border: `1px solid ${T.line}`,
                      background: n === 0 ? T.bgDeep : T.card,
                      color: n === 0 ? T.ink3 : T.ink,
                      borderRadius: T.r.sm, cursor: n === 0 ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><MinusIcon /></button>
                    <div style={{
                      minWidth: 40, height: 40, padding: '0 8px',
                      background: active ? T.forest : 'transparent',
                      color: active ? '#fff' : T.ink3,
                      borderRadius: T.r.sm,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em',
                    }}>{n}</div>
                    <button onClick={() => bump(b.id, 1)} style={{
                      width: 40, height: 40,
                      border: `1px solid ${active ? T.forest : T.lineStrong}`,
                      background: active ? T.forest : T.card,
                      color: active ? '#fff' : T.ink,
                      borderRadius: T.r.sm, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><PlusIcon /></button>
                  </div>
                </div>
              );
            })}
            {visibleList.length === 0 && butterflies.length > 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: T.ink2, fontFamily: T.font, fontSize: 14 }}>
                {seenFilter ? 'No sightings recorded yet.' : `No species match "${search}"`}
              </div>
            )}
            {butterflies.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: T.ink2, fontFamily: T.font, fontSize: 14 }}>
                Loading species…
              </div>
            )}
          </div>
        </div>

        {/* ── Floating timer pill ── */}
        <FloatingTimer
          seconds={secondsElapsed}
          running={timerRunning}
          onToggle={handleTimerToggle}
          total={grandTotal}
          onDone={onSubmit}
        />
      </div>
    </div>
  );
};

export default SurveyRecording;
